'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import {
  EDITIONS, SURAH_IDS, SURAH_META, getArabic, getTransliteration, getTranslation, getAudioUrl, getSurahMeta,
} from '@/lib/quran-data';
import { Play, Pause, Square, Volume2, Bookmark, BookmarkCheck, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function ReadSection() {
  const {
    uiLang, translationEd, setTranslationEd, surah, setSurah,
    nowPlaying, setNowPlaying, markRead, markListened, bookmarks, toggleBookmark,
  } = useApp();
  const t = DICTS[uiLang];
  const [showTranslit, setShowTranslit] = useState(true);
  const [showTranslation, setShowTranslation] = useState(true);
  const [audioLoading, setAudioLoading] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioError, setAudioError] = useState(false);

  const arabic = useMemo(() => getArabic(surah), [surah]);
  const translit = useMemo(() => getTransliteration(surah), [surah]);
  const translation = useMemo(() => getTranslation(surah, translationEd), [surah, translationEd]);
  const meta = getSurahMeta(surah);

  const playingAyah = nowPlaying?.surah === surah ? nowPlaying.ayah : null;

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, [surah]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onEnded = () => {
      setNowPlaying(null);
      setAudioLoading(null);
    };
    const onError = () => {
      setAudioError(true);
      setNowPlaying(null);
      setAudioLoading(null);
    };
    const onPlaying = () => {
      setAudioLoading(null);
      setAudioError(false);
    };
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);
    el.addEventListener('playing', onPlaying);
    return () => {
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
      el.removeEventListener('playing', onPlaying);
    };
  }, [setNowPlaying]);

  const playAyah = (ayahNumber: number, globalNumber: number) => {
    setAudioError(false);
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    const el = audioRef.current;
    if (playingAyah === ayahNumber) {
      el.pause();
      setNowPlaying(null);
      return;
    }
    el.pause();
    el.src = getAudioUrl(useApp.getState().reciter, globalNumber);
    setAudioLoading(ayahNumber);
    setNowPlaying({ surah, ayah: ayahNumber });
    markRead(surah, ayahNumber);
    markListened(surah, ayahNumber);
    el.play().catch(() => {
      setAudioError(true);
      setNowPlaying(null);
      setAudioLoading(null);
    });
  };

  const stopAll = () => {
    audioRef.current?.pause();
    setNowPlaying(null);
  };

  const isBookmarked = (n: number) => bookmarks.some((b) => b.surah === surah && b.ayah === n);

  return (
    <section id="section-read" className="mx-auto max-w-4xl scroll-mt-20 px-4 py-16">
      <header className="mb-8 text-center">
        <h2 className="font-serif-royal text-4xl font-bold text-gold-gradient">{t.read.title}</h2>
        <p className="mt-2 text-sm text-[#cfe0cf]/75">{t.read.subtitle}</p>
      </header>

      {/* Selectors */}
      <div className="gold-ornate-card mb-6 flex flex-col gap-3 rounded-xl p-4 sm:flex-row sm:items-center">
        <label className="flex flex-1 flex-col gap-1.5">
          <span className="text-[11px] font-semibold tracking-wider text-[#d4af37] uppercase">{t.read.select_surah}</span>
          <select
            value={surah}
            onChange={(e) => { stopAll(); setSurah(Number(e.target.value)); }}
            className="gold-scroll rounded-md border border-[rgba(212,175,55,0.35)] bg-[#032019] px-3 py-2.5 text-sm text-[#f5f0e1] outline-none focus:ring-2 focus:ring-[#d4af37]/50"
          >
            {SURAH_IDS.map((s) => {
              const m = SURAH_META[String(s)];
              return (
                <option key={s} value={s}>
                  {s}. {m.nameEn} — {uiLang === 'ar' ? m.nameAr : uiLang === 'en' ? m.meaningEn : m.meaningFr}
                </option>
              );
            })}
          </select>
        </label>
        <label className="flex flex-1 flex-col gap-1.5">
          <span className="text-[11px] font-semibold tracking-wider text-[#d4af37] uppercase">{t.read.select_translation}</span>
          <select
            value={translationEd}
            onChange={(e) => setTranslationEd(e.target.value)}
            className="gold-scroll rounded-md border border-[rgba(212,175,55,0.35)] bg-[#032019] px-3 py-2.5 text-sm text-[#f5f0e1] outline-none focus:ring-2 focus:ring-[#d4af37]/50"
          >
            {EDITIONS.map((e) => (
              <option key={e.id} value={e.id}>{e.flag} {e.native}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Surah header card */}
      <div className="gold-ornate-card mb-6 rounded-2xl p-6 text-center">
        <h3 className="font-arabic text-3xl font-bold text-[#f0d98c]" dir="rtl" lang="ar">{meta.nameAr}</h3>
        <p className="font-serif-royal mt-1 text-xl font-semibold text-[#e8dcc0]">
          {surah}. {meta.nameEn} — {uiLang === 'en' ? meta.meaningEn : meta.meaningFr}
        </p>
        <p className="mt-1 text-xs tracking-wide text-[#cfe0cf]/60">
          {uiLang === 'ar' ? t.read.meccan : meta.revelation === 'Meccan' ? t.read.meccan : t.read.medinan} · {meta.ayahs} {t.read.verses}
        </p>
        {surah !== 1 && (
          <p className="font-arabic mt-4 text-lg text-[#cfe0cf]/85" dir="rtl" lang="ar">
            بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
          </p>
        )}
      </div>

      {/* Toggles */}
      <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
        <button
          onClick={() => setShowTranslation(!showTranslation)}
          className={cn(
            'rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors',
            showTranslation
              ? 'border-[rgba(212,175,55,0.6)] bg-[rgba(212,175,55,0.15)] text-[#f0d98c]'
              : 'border-[rgba(212,175,55,0.25)] text-[#cfe0cf]/60 hover:text-[#f0d98c]'
          )}
        >
          {t.read.translation}
        </button>
        <button
          onClick={() => setShowTranslit(!showTranslit)}
          className={cn(
            'rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors',
            showTranslit
              ? 'border-[rgba(212,175,55,0.6)] bg-[rgba(212,175,55,0.15)] text-[#f0d98c]'
              : 'border-[rgba(212,175,55,0.25)] text-[#cfe0cf]/60 hover:text-[#f0d98c]'
          )}
        >
          {t.read.transliteration}
        </button>
        {playingAyah && (
          <button
            onClick={stopAll}
            className="flex items-center gap-1.5 rounded-full bg-[#e05252]/20 px-4 py-1.5 text-xs font-semibold text-[#ff9b9b] hover:bg-[#e05252]/30"
          >
            <Square className="h-3 w-3" /> {t.read.stop}
          </button>
        )}
      </div>

      {audioError && (
        <p className="mb-4 text-center text-xs text-[#ff9b9b]">{t.read.audio_error}</p>
      )}

      {/* Verses */}
      <div className="space-y-4">
        {arabic.map((a, idx) => {
          const tr = translit[idx];
          const tl = translation[idx];
          const playing = playingAyah === a.n;
          return (
            <article
              key={a.n}
              id={`verse-${surah}-${a.n}`}
              className={cn(
                'gold-ornate-card rounded-xl border-inline-start-2 p-5 transition-all',
                playing && 'ayah-playing'
              )}
              style={{ borderInlineStart: '3px solid rgba(212,175,55,0.25)' }}
            >
              {/* Verse header */}
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[rgba(212,175,55,0.5)] bg-[rgba(212,175,55,0.1)] text-xs font-bold text-[#f0d98c]">
                    {surah}:{a.n}
                  </span>
                  <button
                    onClick={() => playAyah(a.n, a.g)}
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-full transition-all',
                      playing
                        ? 'bg-[#d4af37] text-[#032019]'
                        : 'border border-[rgba(212,175,55,0.4)] text-[#f0d98c] hover:bg-[rgba(212,175,55,0.15)]'
                    )}
                    aria-label={playing ? t.read.pause : t.read.play_verse}
                  >
                    {playing ? (
                      audioLoading === a.n ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4 rtl-flip" />
                    )}
                  </button>
                  <Volume2 className={cn('h-4 w-4 text-[#d4af37]', playing ? 'animate-pulse' : 'opacity-30')} aria-hidden />
                </div>
                <button
                  onClick={() => toggleBookmark(surah, a.n)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-[#d4af37]/60 transition-colors hover:bg-[rgba(212,175,55,0.1)] hover:text-[#d4af37]"
                  aria-label={isBookmarked(a.n) ? t.tools.remove_bookmark : t.tools.add_bookmark}
                >
                  {isBookmarked(a.n) ? <BookmarkCheck className="h-4 w-4 text-[#d4af37]" /> : <Bookmark className="h-4 w-4" />}
                </button>
              </div>

              {/* Arabic */}
              <p className="font-arabic mb-4 text-right text-2xl leading-[2.1] text-[#f5f0e1] sm:text-[1.7rem]" dir="rtl" lang="ar">
                {a.text}
              </p>

              {/* Transliteration */}
              {showTranslit && tr && (
                <p className="mb-2 border-t border-[rgba(212,175,55,0.12)] pt-3 text-sm italic text-[#e8c86a]/90">{tr.text}</p>
              )}

              {/* Translation */}
              {showTranslation && tl && (
                <p
                  className={tl.text.match(/[\u0600-\u06FF]/) ? 'text-right text-base leading-loose' : 'text-sm leading-relaxed'}
                  dir={tl.text.match(/[\u0600-\u06FF]/) ? 'rtl' : undefined}
                  style={{ textAlign: tl.text.match(/[\u0600-\u06FF]/) ? 'right' : uiLang === 'ar' ? 'right' : 'left' }}
                >
                  {tl.text}
                </p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
